import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Card, Badge, Button, Row, Col } from "react-bootstrap";
import { FaMapMarkerAlt, FaPhone, FaWhatsapp, FaClock, FaIdCard, FaCalendarAlt, FaStar, FaExclamationTriangle, FaArrowLeft } from "react-icons/fa";
import { FaClipboardList, FaGlobe, FaHandsHelping, FaUsers, FaShieldAlt, FaCogs } from "react-icons/fa";
import Navbar from '../../Pages/Navbar/JobSeekerNavbar';
import defaultImage from "../../Pages/Images/young-beautiful-teacher-toddlers-playing-260nw-1690362274.webp";
import "./individualTraining.css";
import BaseUrl from '../../ApiServices/BaseUrl';

const IndividualTraining = () => {
  const [training, setTraining] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const trainingId = searchParams.get('id');

  useEffect(() => {
    if (trainingId) {
      fetchTrainingDetails();
    }
  }, [trainingId]);

  const fetchTrainingDetails = async () => {
    try {
      const response = await fetch(`${BaseUrl}/trainings/${trainingId}`);
      if (!response.ok) {
        throw new Error('Failed to fetch training details');
      }
      const data = await response.json();
      console.log('Training details:', data); // Debug log
      setTraining(data);
      setLoading(false);
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate('/jobseeker-training');
  };

  // Improved image error handler
  const handleImageError = (e, imageType) => {
    console.log(`Image error for ${imageType}:`, e.target.src);
    e.target.src = defaultImage;
    e.target.onerror = null; // Prevent infinite loop
  };

  // Check if image URL is valid
  const isValidImageUrl = (url) => {
    return url && url !== 'null' && url !== 'undefined' && !url.includes('null') && url !== defaultImage;
  };

  // Get image source with validation
  const getImageSource = (url, imageType) => {
    if (isValidImageUrl(url)) {
      return url;
    }
    return defaultImage;
  };

  if (loading) {
    return (
      <>
        <Navbar />
        <div className="container mt-4">
          <div className="text-center mt-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-2">Loading training details...</p>
          </div>
        </div>
      </>
    );
  }

  if (error) {
    return (
      <>
        <Navbar />
        <div className="container mt-4">
          <div className="alert alert-danger text-center mt-5" role="alert">
            Error: {error}
          </div>
          <Button variant="primary" onClick={handleBack} className="mt-3">
            <FaArrowLeft className="me-2" />
            Back to Trainings
          </Button>
        </div>
      </>
    );
  }

  if (!training) {
    return (
      <>
        <Navbar />
        <div className="container mt-4">
          <div className="alert alert-warning text-center mt-5" role="alert">
            Training not found
          </div>
          <Button variant="primary" onClick={handleBack} className="mt-3">
            <FaArrowLeft className="me-2" />
            Back to Trainings
          </Button>
        </div>
      </>
    );
  }

  return (
    <>
      <Navbar />
      <div className="individual-training-container" style={{marginTop:"80px"}}>
        {/* Back Button */}
       <div className="container">
          <Button 
            variant="outline-primary" 
            onClick={handleBack} 
            className="back-to-trainings-btn mb-3" // Added custom class
          >
            <FaArrowLeft className="me-2" />
            Back to Trainings
          </Button>
        </div>

        {/* Main Training Card */}
        <Card className="clutter-card-training shadow-sm border-0 rounded-3 overflow-hidden mb-4">
          <div className="clutter-banner">
            <img 
              src={getImageSource(training.back_profile, 'banner')}
              alt={`${training.training_title} banner`} 
              className="banner-img" 
              onError={(e) => handleImageError(e, 'banner')}
              loading="lazy"
            />
            <div className="clutter-logo">
              <img 
                src={getImageSource(training.profile_image, 'logo')}
                alt={`${training.training_title} logo`} 
                onError={(e) => handleImageError(e, 'logo')}
                loading="lazy"
              />
            </div>
          </div>

          <Card.Body className="clutter-body">
            <div className="d-flex align-items-center mb-3">
              <div className="flex-grow-1">
                <h4 className="mb-1 text-primary fw-bold">{training.training_title}</h4>
                <Badge bg="warning" text="dark" className="px-2 py-1">
                  <FaStar className="me-1" />
                  {training.rating || 'No'} Rating
                </Badge>
              </div>
              <div className="ms-auto d-flex align-items-center text-danger fw-semibold">
                <FaMapMarkerAlt className="me-1" />
                <span>{training.location || 'Location not specified'}</span>
              </div>
            </div>

            <p className="text-muted mb-4">
              {training.description || 'No description available.'}
            </p>

            <h6 className="fw-bold mb-2">Our Services</h6>
            <p className="fw-semibold text-primary mb-4">"{training.our_services || 'General Training Services'}"</p>

            {/* Additional training details */}
            <div className="training-details mt-4">
              <h6 className="fw-bold mb-3">Training Overview</h6>
              <p className="text-muted">
                {training.description || 'This training program is designed to provide comprehensive skills and knowledge to domestic workers. Our certified trainers ensure quality education and practical experience.'}
              </p>
            </div>
          </Card.Body>
        </Card>

        {/* ================== Services Section ================== */}
        <section className="services-section my-5">
          <div className="container">
            <h4 className="fw-bold text-primary text-center mb-4">Services</h4>
            <Row className="g-4 justify-content-center">
              {/* Online Training Card */}
              <Col md={6} lg={4}>
                <Card className="service-card h-100 shadow-sm border-0 rounded-3">
                  <Card.Body className="text-center p-4">
                    <div className="service-icon bg-primary text-white rounded-circle mx-auto mb-3 d-flex align-items-center justify-content-center">
                      <FaUsers size={24} />
                    </div>
                    <h5 className="fw-semibold text-primary">Online Training</h5>
                    <p className="text-muted mb-2">Flexible learning from anywhere</p>
                    <div className="price-tag bg-light rounded-pill px-3 py-1 mb-3">
                      <span className="fw-bold text-primary">From HK$ 350</span>
                    </div>
                  </Card.Body>
                </Card>
              </Col>

              {/* Our Services Card */}
              <Col md={6} lg={4}>
                <Card className="service-card h-100 shadow-sm border-0 rounded-3">
                  <Card.Body className="text-center p-4">
                    <div className="service-icon bg-success text-white rounded-circle mx-auto mb-3 d-flex align-items-center justify-content-center">
                      <FaHandsHelping size={24} />
                    </div>
                    <h5 className="fw-semibold text-primary">{training.our_services || 'Professional Training'}</h5>
                    <p className="text-muted mb-2">Professional training services</p>
                    <div className="price-tag bg-light rounded-pill px-3 py-1 mb-3">
                      <span className="fw-bold text-primary">Contact for pricing</span>
                    </div>
                  </Card.Body>
                </Card>
              </Col>

              {/* Additional Service Card */}
              <Col md={6} lg={4}>
                <Card className="service-card h-100 shadow-sm border-0 rounded-3">
                  <Card.Body className="text-center p-4">
                    <div className="service-icon bg-info text-white rounded-circle mx-auto mb-3 d-flex align-items-center justify-content-center">
                      <FaCogs size={24} />
                    </div>
                    <h5 className="fw-semibold text-primary">Custom Training</h5>
                    <p className="text-muted mb-2">Tailored programs for specific needs</p>
                    <div className="price-tag bg-light rounded-pill px-3 py-1 mb-3">
                      <span className="fw-bold text-primary">Custom Quote</span>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </div>
        </section>

        {/* ================== How We Can Help You Section ================== */}
        <section className="help-section my-5">
          <div className="container">
            <h4 className="fw-bold text-primary text-center mb-4">
              How We Can Help You
            </h4>

            <Row className="g-4 justify-content-center">
              {/* Our Strength Card */}
              <Col md={6} lg={5}>
                <Card className="help-card shadow-sm border-0 rounded-3 h-100">
                  <Card.Body className="p-4">
                    <div className="d-flex align-items-center mb-3">
                      <div className="help-icon bg-warning text-dark rounded-circle d-flex align-items-center justify-content-center me-3">
                        <FaShieldAlt size={20} />
                      </div>
                      <h5 className="fw-bold mb-0">Our Strength</h5>
                    </div>
                    <div className="strength-badges">
                      <Badge bg="light" text="dark" className="me-2 mb-2 p-2">Supportive</Badge>
                      <Badge bg="light" text="dark" className="me-2 mb-2 p-2">Results Oriented</Badge>
                      <Badge bg="light" text="dark" className="me-2 mb-2 p-2">Professional</Badge>
                      <Badge bg="light" text="dark" className="me-2 mb-2 p-2">Ethical</Badge>
                      <Badge bg="light" text="dark" className="me-2 mb-2 p-2">Flexible</Badge>
                      <Badge bg="light" text="dark" className="me-2 mb-2 p-2">Certified</Badge>
                    </div>
                  </Card.Body>
                </Card>
              </Col>

              {/* We Speak Card */}
              <Col md={6} lg={5}>
                <Card className="help-card shadow-sm border-0 rounded-3 h-100">
                  <Card.Body className="p-4">
                    <div className="d-flex align-items-center mb-3">
                      <div className="help-icon bg-info text-white rounded-circle d-flex align-items-center justify-content-center me-3">
                        <FaGlobe size={20} />
                      </div>
                      <h5 className="fw-bold mb-0">We Speak</h5>
                    </div>
                    <div className="language-badges">
                      <Badge bg="primary" className="me-2 mb-2 p-2 fs-6">
                        English
                      </Badge>
                      <Badge bg="success" className="me-2 mb-2 p-2 fs-6">
                        Cantonese
                      </Badge>
                      <Badge bg="warning" text="dark" className="me-2 mb-2 p-2 fs-6">
                        Mandarin
                      </Badge>
                      <Badge bg="secondary" className="me-2 mb-2 p-2 fs-6">
                        Tagalog
                      </Badge>
                      <Badge bg="danger" className="me-2 mb-2 p-2 fs-6">
                        Indonesian
                      </Badge>
                    </div>
                    <p className="text-muted mt-2 mb-0">Multilingual support for better communication</p>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </div>
        </section>

        {/* ================== Contact Detail Section ================== */}
        <section className="contact-section my-5">
          <div className="container">
            <h4 className="fw-bold text-primary text-center mb-4">Contact Detail</h4>
            
            <Row className="justify-content-center">
              {/* Contact Details Card - Now centered and full width */}
              <Col md={8} lg={6}>
                <Card className="contact-detail-card shadow-sm border-0 rounded-3">
                  <Card.Body className="p-4">
                    <div className="d-flex align-items-center mb-4">
                      <div className="contact-main-icon bg-primary text-white rounded-circle d-flex align-items-center justify-content-center me-3">
                        <FaIdCard size={24} />
                      </div>
                      <h5 className="fw-bold mb-0 text-primary">Contact Information</h5>
                    </div>

                    {/* Address */}
                    <div className="contact-item d-flex align-items-center mb-3">
                      <div className="contact-icon bg-primary text-white rounded-circle d-flex align-items-center justify-content-center me-3">
                        <FaMapMarkerAlt size={16} />
                      </div>
                      <div>
                        <h6 className="fw-bold mb-1">Address</h6>
                        <p className="text-muted mb-0">{training.location || '8 Finance St, Central, Hong Kong'}</p>
                      </div>
                    </div>

                    {/* Phone */}
                    <div className="contact-item d-flex align-items-center mb-3">
                      <div className="contact-icon bg-success text-white rounded-circle d-flex align-items-center justify-content-center me-3">
                        <FaPhone size={14} />
                      </div>
                      <div>
                        <h6 className="fw-bold mb-1">Phone</h6>
                        <p className="text-muted mb-0">+852-95284866</p>
                      </div>
                    </div>

                    {/* WhatsApp */}
                    <div className="contact-item d-flex align-items-center mb-3">
                      <div className="contact-icon bg-success text-white rounded-circle d-flex align-items-center justify-content-center me-3">
                        <FaWhatsapp size={16} />
                      </div>
                      <div>
                        <h6 className="fw-bold mb-1">WhatsApp</h6>
                        <p className="text-muted mb-0">+852-95284866</p>
                      </div>
                    </div>

                    {/* Time */}
                    <div className="contact-item d-flex align-items-center mb-3">
                      <div className="contact-icon bg-warning text-dark rounded-circle d-flex align-items-center justify-content-center me-3">
                        <FaClock size={14} />
                      </div>
                      <div>
                        <h6 className="fw-bold mb-1">Time</h6>
                        <p className="text-muted mb-0">09:00 AM to 06:00 PM</p>
                      </div>
                    </div>

                    {/* Licence */}
                    <div className="contact-item d-flex align-items-center mb-3">
                      <div className="contact-icon bg-info text-white rounded-circle d-flex align-items-center justify-content-center me-3">
                        <FaIdCard size={14} />
                      </div>
                      <div>
                        <h6 className="fw-bold mb-1">Licence</h6>
                        <p className="text-muted mb-0">N/A</p>
                      </div>
                    </div>

                    {/* Day Off */}
                    <div className="contact-item d-flex align-items-center">
                      <div className="contact-icon bg-secondary text-white rounded-circle d-flex align-items-center justify-content-center me-3">
                        <FaCalendarAlt size={14} />
                      </div>
                      <div>
                        <h6 className="fw-bold mb-1">Day Off</h6>
                        <p className="text-muted mb-0">N/A</p>
                      </div>
                    </div>

                    {/* Report this Training - Moved to bottom of contact card */}
                    {/* <div className="report-section text-center mt-4 pt-3 border-top">
                      <Button variant="outline-danger" className="px-4 py-2">
                        <FaExclamationTriangle className="me-2" />
                        Report this Training
                      </Button>
                    </div> */}
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </div>
        </section>
      </div>
    </>
  );
};

export default IndividualTraining;